// SerialTable.js
import React, { useState, useEffect } from "react";
import { Table, Input, Button, Space, message, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { readItems, aggregate } from "@directus/sdk";
import { getDirectusClient } from "../../lib/directus";
import Layouts from "../../components/Layouts";
import SerialDetailModal from "./components/SerialDetailModal";

const getStatusColor = (estado) => {
  switch (estado?.toLowerCase()) {
    case 'activo':
      return 'green';
    case 'facturado':
      return 'blue';
    case 'prestado':
      return 'orange';
    case 'anulado':
      return 'red';
    default:
      return 'default';
  }
};

const SerialTable = () => {
  const [serials, setSerials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [transferHistoryLoading, setTransferHistoryLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSerial, setSelectedSerial] = useState(null);
  const [transferHistory, setTransferHistory] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");
  const [serialHistory, setSerialHistory] = useState([]);
  const [serialHistoryLoading, setSerialHistoryLoading] = useState(false);
  const [orderHistory, setOrderHistory] = useState([]);
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);

  const fetchSerials = async (page = 1, pageSize = 25, search = "") => {
    setLoading(true);
    try {
      const directus = await getDirectusClient();

      const response = await directus.request(
        readItems("seriales", {
          fields: [
            "id",
            "serial",
            "estado.estado",
            "bodega.nombre",
            "producto.id",
            "producto.nombre",
            "producto.preciosinimp",
            "producto.grupo.impuesto",
          
            "orden_compra_ingreso.id",
            "orden_compra_ingreso.numero_factura",
            "orden_compra_ingreso.date_created",
            "orden_compra_ingreso.date_updated",
            "orden_compra_ingreso.user_created.first_name",
            "orden_compra_ingreso.ingresado_por.first_name",
            "orden_compra_ingreso.estado.nombre",
            "orden_compra_ingreso.proveedor.nombre",
          ],
          sort: ["-date_created"],
          limit: pageSize,
          page: page,
          filter: search
            ? {
                serial: { _contains: search },
              }
            : {},
        })
      );

      const totalResponse = await directus.request(
        aggregate("seriales", {
          aggregate: {
            countDistinct: ["*"],
          },
          filter: search
            ? {
                serial: { _contains: search },
              }
            : {},
        })
      );

      if (totalResponse && totalResponse.length > 0) {
        setPagination({
          ...pagination,
          current: page,
          pageSize,
          total: parseInt(totalResponse[0].countDistinct["*"], 10),
        });
      } else {
        console.error(
          "La respuesta de aggregate no tiene la estructura esperada:",
          totalResponse
        );
        message.error("Error al obtener el total de registros");
      }

      setSerials(response);
    } catch (error) {
      console.error("Error fetching serials:", error);
      message.error("Error al cargar los seriales");
    } finally {
      setLoading(false);
    }
  };

  const fetchTransferHistory = async (serialId) => {
    setTransferHistoryLoading(true);
    try {
      const directus = await getDirectusClient();

      const response = await directus.request(
        readItems("historial_traslado", {
          fields: [
            "id",
            "estado",
            "date_created",
            "aceptado_fecha",

            "bodega_salida.nombre",
            "bodega_destino.nombre",

            "acaptado_por.first_name",
            "acaptado_por.last_name",
            "acaptado_por.email",
            "anulado_por.first_name",
            "anulado_por.last_name",
            "anulado_por.email",
          ],
          filter: {
            serial: {
              seriales_id: {
              _in: [serialId],
              }
            },
          },
          sort: ["-date_created"],
        })
      );

      setTransferHistory(response);
    } catch (error) {
      console.error("Error fetching transfer history:", error);
      message.error("Error al cargar el historial de traslados");
    } finally {
      setTransferHistoryLoading(false);
    }
  };

  const fetchSerialHistory = async (serialId) => {
    setSerialHistoryLoading(true);
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readItems("historial_seriales", {
          fields: [
            "id",
            "factura_n.id",
            "prestamo.id",
            "tipo.tipo",
            "tipo.id",
            "serial",
            "date_created",
            "date_updated",
         
          ],
          filter: {
            seriales: {
              seriales_id: {
              _in: [serialId],
              }
            },
          },
          sort: ["-date_created"],
        })
      );
      setSerialHistory(response);
    } catch (error) {
      console.error("Error fetching serial history:", error);
      message.error("Error al cargar el historial del serial");
    } finally {
      setSerialHistoryLoading(false);
    }
  };

  const fetchOrderHistory = async (serialId) => {
    setOrderHistoryLoading(true);
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readItems("ordenes_decompra", {
          fields: [
            "id",
            "estado.nombre",
            "proveedor.nombre",
            "ingresado_por.first_name",
            "user_created.first_name",
            "date_created",
            "date_updated",
            "productos.producto_detalle.nombre",
            "productos.cantidad",
            "productos.valor_sim",
            "productos.precio_compra",
            "productos.precio_uni_final",
            "productos.descuento_comercial"
          ],
          filter: {
            productos: {
              seriales: {
                id: {
                  _eq: serialId
                }
              }
            }
          }
        })
      );
      setOrderHistory(response);
    } catch (error) {
      console.error("Error fetching order history:", error);
      message.error("Error al cargar el historial de órdenes");
    } finally {
      setOrderHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchSerials(pagination.current, pagination.pageSize, searchText);
  }, []);

  const handleTableChange = (pagination) => {
    setPagination(pagination);
    fetchSerials(pagination.current, pagination.pageSize, searchText);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchSerials(1, pagination.pageSize, value);
  };

  const handleViewSerial = async (record) => {
    setSelectedSerial(record);
    setModalVisible(true);

    // Fetch all histories in parallel
    await Promise.all([
      fetchTransferHistory(record.id),
      fetchSerialHistory(record.id),
      fetchOrderHistory(record.id)
    ]);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedSerial(null);
    setTransferHistory([]);
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Serial",
      dataIndex: "serial",
      key: "serial",
    },
    {
      title: "Estado",
      dataIndex: ["estado", "estado"],
      key: "estado",
      render: (estado) => (
        <Tag color={getStatusColor(estado)}>
          {estado?.toUpperCase() || "N/A"}
        </Tag>
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_, record) => (
        <Button type="link" onClick={() => handleViewSerial(record)}>
          Ver Serial
        </Button>
      ),
    },
  ];

  return (
    <Layouts>
      {/* Filtro de búsqueda */}
      <Space style={{ marginBottom: 16 }}>
        <Input
          placeholder="Buscar por serial"
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          prefix={<SearchOutlined />}
        />
      </Space>

      {/* Tabla */}
      <Table
        columns={columns}
        dataSource={serials}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50"],
        }}
        onChange={handleTableChange}
      />

      {/* Modal de Detalles */}
      <SerialDetailModal
        visible={modalVisible}
        onClose={handleCloseModal}
        selectedSerial={selectedSerial}
        transferHistory={transferHistory}
        transferHistoryLoading={transferHistoryLoading}
        serialHistory={serialHistory}
        serialHistoryLoading={serialHistoryLoading}
        orderHistory={orderHistory}
        orderHistoryLoading={orderHistoryLoading}
      />
    </Layouts>
  );
};

export default SerialTable;
